import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PortalDialogContent as DialogContent } from './PortalDialogContent';
import portalApi, { scopedPortalApi } from './lib/portalApi';
import { usePortalAuth } from './PortalAuthContext';
import { parsePortalPath, destinationPath } from './lib/portalPaths';
import { buildPharmacyTokens } from './lib/portalTheme';
import { usePortalTheme } from './PortalThemeContext';
import { PortalSplash } from './ui/PortalSplash';
import { recordPharmacyVisit } from './lib/recentPharmacies';
import './portalBrand.css';

const Context = createContext(null);

export const PortalDestinationProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const parsed = useMemo(() => parsePortalPath(location.pathname, location.search), [location.pathname, location.search]);
  const [resolved, setResolved] = useState(null);
  const [distances, setDistances] = useState({}); // branch tenant_id -> distance_km, merged at render time (see destination below)
  const [error, setError] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [directoryError, setDirectoryError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [draftSummary, setDraftSummary] = useState(''); // what the switch-confirmation dialog shows, e.g. "3 items: Panadol, …"
  const [busy, setBusy] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [consent, setConsent] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { theme } = usePortalTheme();
  const consentResolve = useRef(null);
  const userClearedBranch = useRef(false);
  const selected = !!(parsed.pharmacySlug || parsed.pharmacyId || parsed.branchId);
  // Merge in the last-known distance per branch (from the opportunistic geo
  // effect below) at render time, rather than writing it into `resolved`
  // directly — a later navigation (e.g. a bare pharmacy URL redirecting to
  // its /login page) re-runs the main resolve() effect below WITHOUT lat/lng
  // and would otherwise silently overwrite/discard the distance that already
  // landed, regardless of which request happens to finish last.
  const destination = useMemo(() => {
    const base = resolved?.key === parsed.key ? resolved.data : null;
    if (!base || !Object.keys(distances).length) return base;
    const withDistance = b => (distances[b.tenant_id] != null ? { ...b, distance_km: distances[b.tenant_id] } : b);
    return { ...base, branches: (base.branches || []).map(withDistance), branch: base.branch ? withDistance(base.branch) : base.branch };
  }, [resolved, parsed.key, distances]);
  const api = useMemo(() => scopedPortalApi(destination), [destination]);
  const { patient, favourites } = usePortalAuth();

  const trackEngagement = (event, pharmacyId, branchId) => {
    if (!pharmacyId) return;
    portalApi.post('/engagement', { event, pharmacy_id: pharmacyId, branch_id: branchId || null }).catch(() => {});
  };

  useEffect(() => {
    let live = true;
    portalApi.get('/destinations').then(r => { if (live) setDirectory(r.data.pharmacies); })
      .catch(() => { if (live) setDirectoryError('Pharmacies could not be loaded. Please try again.'); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    setError(null); setDirty(false); setBusy(false); setPendingPath(null);
    if (selected && !parsed.invalid) {
      portalApi.get('/destinations/resolve', { params: {
        pharmacy_slug: parsed.pharmacySlug || undefined, branch_slug: parsed.branchSlug || undefined,
        pharmacy_id: parsed.pharmacyId || undefined, branch_id: parsed.branchId || undefined,
      } }).then(r => {
        if (!live) return;
        if (r.data.redirected && r.data.canonical_path) {
          const target = `${r.data.canonical_path}${parsed.page ? `/${parsed.page}` : ''}${parsed.suffix || ''}`;
          navigate(target, { replace: true });
          return;
        }
        setResolved({ key: parsed.key, data: r.data });
      })
        .catch(e => { if (live) setError({ key: parsed.key, message: e.response?.status === 404
          ? 'This pharmacy or branch link is unavailable.' : 'We could not load this portal. Please try again.' }); });
    }
    return () => { live = false; consentResolve.current?.(false); consentResolve.current = null; setConsent(false); };
    // Only identity changes should unmount request forms, not normal page navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed.key, parsed.invalid, selected]);

  // Reset the "user cleared branch" guard when the pharmacy itself changes.
  // Keyed on pharmacy identity only — NOT on branch changes.
  const pharmacyIdentity = parsed.pharmacySlug || parsed.pharmacyId;
  useEffect(() => { userClearedBranch.current = false; }, [pharmacyIdentity]);


  useEffect(() => {
    if (!destination?.pharmacy) return;
    const day = new Date().toISOString().slice(0, 10);
    const identity = `${destination.pharmacy.tenant_id}:${destination.branch?.tenant_id || 'all'}`;
    const source = new URLSearchParams(location.search).get('source');
    for (const event of source === 'qr' ? ['visit', 'qr_open'] : ['visit']) {
      const key = `portal-engagement:${day}:${identity}:${event}`;
      if (sessionStorage.getItem(key)) continue;
      sessionStorage.setItem(key, '1');
      trackEngagement(event, destination.pharmacy.tenant_id, destination.branch?.tenant_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination?.pharmacy?.tenant_id, destination?.branch?.tenant_id]);

  // Device-local "recently visited" list (Phase 1 pharmacy-switcher shortcut) —
  // bumped on every real landing, independent of the once-per-day engagement log above.
  useEffect(() => {
    if (destination?.pharmacy?.tenant_id) recordPharmacyVisit(destination.pharmacy.tenant_id);
  }, [destination?.pharmacy?.tenant_id]);

  // Opportunistic distance for the branch switcher: only reads location if the
  // browser already granted it (e.g. via "Find a pharmacy"'s "Use my location"),
  // never prompts on its own. Open/closed status needs no location and already
  // arrives with the initial resolve() call above.
  const [geo, setGeo] = useState(null);
  useEffect(() => {
    if (!navigator.permissions || !navigator.geolocation) return;
    let live = true;
    navigator.permissions.query({ name: 'geolocation' }).then(status => {
      if (!live || status.state !== 'granted') return;
      navigator.geolocation.getCurrentPosition(
        pos => { if (live) setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        () => {}, { timeout: 8000, maximumAge: 300000 },
      );
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  // Fetches distance_km per branch once location lands, keyed by branch
  // tenant_id (not by request/path identity) so it survives any later
  // redirect or re-resolve of the main effect above — see the `destination`
  // merge for why this can't just overwrite `resolved` directly.
  useEffect(() => {
    if (!geo || !destination?.pharmacy) return;
    let live = true;
    portalApi.get('/destinations/resolve', { params: {
      pharmacy_slug: parsed.pharmacySlug || undefined, branch_slug: parsed.branchSlug || undefined,
      pharmacy_id: parsed.pharmacyId || undefined, branch_id: parsed.branchId || undefined,
      lat: geo.lat, lng: geo.lng,
    } }).then(r => {
      if (!live) return;
      const next = {};
      for (const b of r.data.branches || []) {
        if (b.distance_km != null) next[b.tenant_id] = b.distance_km;
      }
      if (Object.keys(next).length) setDistances(prev => ({ ...prev, ...next }));
    }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, destination?.pharmacy?.tenant_id]);

  // A signed-in patient with a home branch lands on it directly: without this a
  // multi-branch pharmacy shows "choose a service branch" and every branch-scoped
  // page (shop, chat, refills) looks empty.
  // Skip if the user explicitly chose "All branches" this session.
  useEffect(() => {
    if (!destination?.pharmacy || destination.branch || !patient) return;
    if (userClearedBranch.current) return;
    const branches = destination.branches || [];
    const home = branches.find(b => b.tenant_id === favourites.preferred_branch_id)
      || branches.find(b => b.tenant_id === patient.preferred_tenant_id)
      || (branches.length === 1 ? branches[0] : null);
    if (!home) return;
    const [basePath, baseQuery] = destinationPath(destination.pharmacy, home, parsed.page).split('?');
    const query = new URLSearchParams(baseQuery || '');
    new URLSearchParams(location.search).forEach((value, key) => {
      if (key !== 'pharmacy' && key !== 'branch' && !query.has(key)) query.set(key, value);
    });
    navigate(`${basePath}${query.toString() ? `?${query}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, patient?.patient_user_id, favourites.preferred_branch_id, parsed.page]);

  const changeDestination = (pharmacy, branch = null) => {    if (busy) { toast.info('Please wait for your current request to finish.'); return; }
    // Track when user explicitly clears the branch ("All branches")
    // so the auto-home-branch effect doesn't override their choice.
    if (!branch && pharmacy?.tenant_id === destination?.pharmacy?.tenant_id) {
      userClearedBranch.current = true;
    } else {
      userClearedBranch.current = false;
    }
    // Preserve non-identity query params (e.g. ?upload=1 opening a flow) across the
    // identity change — only pharmacy/branch selection is owned by destinationPath.
    const [basePath, baseQuery] = destinationPath(pharmacy, branch, parsed.page).split('?');
    const query = new URLSearchParams(baseQuery || '');
    new URLSearchParams(location.search).forEach((value, key) => {
      if (key !== 'pharmacy' && key !== 'branch' && !query.has(key)) query.set(key, value);
    });
    const path = basePath + (query.toString() ? `?${query}` : '');
    if (path === location.pathname + location.search) return;
    if (branch) trackEngagement('branch_selection', pharmacy.tenant_id, branch.tenant_id);
    if (dirty) setPendingPath(path);
    else navigate(path);
  };
  const ensureConnection = async () => {
    if (!destination?.branch) { toast.error('Choose a pharmacy and service branch first.'); return false; }
    try {
      const r = await portalApi.get(`/connections/${destination.pharmacy.tenant_id}`);
      if (r.data.connected) return true;
      return await new Promise(resolve => { consentResolve.current = resolve; setConsent(true); });
    } catch { toast.error('Could not check your pharmacy connection. Please try again.'); return false; }
  };
  const closeConsent = (accepted = false) => {
    consentResolve.current?.(accepted); consentResolve.current = null; setConsent(false);
  };
  const connect = async () => {
    setConnecting(true);
    try {
      await portalApi.post('/connections', { pharmacy_id: destination.pharmacy.tenant_id,
        branch_id: destination.branch.tenant_id, consent: true });
      closeConsent(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not connect. Please try again.'); }
    finally { setConnecting(false); }
  };

  if (parsed.invalid || (error?.key === parsed.key)) return (
    <main className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="portal-unavailable">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-bold">Portal unavailable</h1>
        <p data-testid="portal-unavailable-message">{error?.message || 'This portal address is not available.'}</p>
        <Link to="/portal/find" className="underline" data-testid="portal-unavailable-shared-link">Find a pharmacy</Link>
      </div>
    </main>
  );
  if (selected && !destination) return <PortalSplash testId="portal-destination-loading" />;

  const value = { ...destination, directory, directoryError, api, parsed, href: parsed.href,
    changeDestination, ensureConnection, setDirty, setBusy, busy, setDraftSummary,
    pickerOpen, openPicker: () => setPickerOpen(true), closePicker: () => setPickerOpen(false),
    destinationLabel: destination?.branch
      ? (destination.branch.name.startsWith(destination.pharmacy.name)
          ? destination.branch.name
          : `${destination.pharmacy.name} · ${destination.branch.name}`)
      : '',
  };
  return (
    <Context.Provider value={value}>
      <div className="portal-brand" style={buildPharmacyTokens(destination?.pharmacy, theme)}>
        <div key={parsed.key}>{children}</div>
        <Dialog open={!!pendingPath} onOpenChange={open => { if (!open) setPendingPath(null); }}>
          <DialogContent data-testid="portal-change-destination-dialog">
            <DialogHeader><DialogTitle>{parsed.page === 'shop' ? 'Change collection branch?' : 'Discard this unsent request?'}</DialogTitle>
              <DialogDescription>
                {parsed.page === 'shop' ? 'Your cart belongs to its original branch. Switching will recheck products, prices and availability before you accept a new cart. Nothing is silently substituted.' : 'Changing location clears your draft. Previously sent requests stay with their original branch.'}
                {draftSummary && <span className="block mt-2 text-sm font-medium text-slate-800" data-testid="portal-change-destination-summary">
                  {parsed.page === 'shop' ? 'Will be rechecked: ' : 'Will be discarded: '}{draftSummary}
                </span>}
              </DialogDescription></DialogHeader>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" data-testid="portal-change-destination-cancel" onClick={() => setPendingPath(null)}>Keep editing</Button>
              <Button data-testid="portal-change-destination-confirm" onClick={() => { setDirty(false); navigate(pendingPath); setPendingPath(null); }}>{parsed.page === 'shop' ? 'Switch and review cart' : 'Discard and switch'}</Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={consent} onOpenChange={open => { if (!open && !connecting) closeConsent(false); }}>
          <DialogContent data-testid="portal-connection-dialog">
            <DialogHeader><ShieldCheck className="w-8 h-8 text-emerald-700" />
              <DialogTitle>Connect with {destination?.pharmacy.name}?</DialogTitle>
              <DialogDescription>Your request will go to {destination?.branch?.name}.</DialogDescription></DialogHeader>
            <p className="text-sm leading-relaxed" data-testid="portal-connection-sharing-notice">This pharmacy will receive your name, contact details and the prescriptions, refill requests or messages you choose to send. Your personal health profile and records at other pharmacies are not shared.</p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" disabled={connecting} data-testid="portal-connection-cancel" onClick={() => closeConsent(false)}>Not now</Button>
              <Button disabled={connecting} data-testid="portal-connection-confirm" onClick={connect}>{connecting ? 'Connecting…' : 'Agree and continue'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Context.Provider>
  );
};

export const usePortalDestination = () => useContext(Context);

export const usePortalDraft = (hasDraft, submitting = false, summary = '') => {
  const { setDirty, setBusy, setDraftSummary } = usePortalDestination();
  useEffect(() => { setDirty(!!hasDraft); return () => setDirty(false); }, [hasDraft, setDirty]);
  useEffect(() => { setBusy(submitting); return () => setBusy(false); }, [submitting, setBusy]);
  // Shown verbatim in the switch-confirmation dialog so a patient sees exactly
  // what will be discarded/rechecked instead of a generic warning.
  useEffect(() => { setDraftSummary(hasDraft ? summary : ''); return () => setDraftSummary(''); }, [hasDraft, summary, setDraftSummary]);
};